import { ArchiveIcon, WarningCircleIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

import type { SearchLimitContext } from '@entities/search-session';
import { Button } from '@shared/ui';

import { formatActivity } from '../model/searchStatus';
import styles from './Searches.module.css';

interface SearchLimitNoticeProps {
  context: SearchLimitContext;
  pending: boolean;
  onArchive: (searchId: string) => void;
}

/**
 * Экран лимита запросов (FE-152, MVP_PRD §20, AC-08).
 *
 * Два правила, которые здесь нельзя нарушить:
 *
 * * **система не архивирует запросы сама.** Список кандидатов — это
 *   предложение, каждое архивирование выполняет пользователь отдельной
 *   кнопкой. Никакого «освободить место автоматически»;
 * * **число в тексте берётся из ответа сервера**, а не хардкодится: лимит
 *   живёт в backend-константе, и две копии неизбежно разойдутся.
 */
export function SearchLimitNotice({ context, pending, onArchive }: SearchLimitNoticeProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'de';

  return (
    <section className={styles.limit} role="alert" aria-live="polite">
      {/* «Требует внимания»: оранжевая рамка дополнена иконкой и текстом —
          цвет не единственный носитель смысла (DESIGN_SYSTEM §2.4). */}
      <p className={styles.limitTitle}>
        <WarningCircleIcon size={18} aria-hidden="true" className={styles.limitIcon} />
        {t('searches.limit.title', { count: context.limit })}
      </p>
      <p className={styles.limitText}>{t('searches.limit.text')}</p>

      {context.archive_candidates.length > 0 ? (
        <ul className={styles.candidates} aria-label={t('searches.limit.candidates')}>
          {context.archive_candidates.map((candidate) => (
            <li key={candidate.id} className={styles.candidate}>
              <span>
                {candidate.title ?? t('searches.untitled')}
                <span className={styles.candidateMeta}>
                  {' · '}
                  {t('searches.card.activity', {
                    value: formatActivity(candidate.last_activity_at, locale),
                  })}
                </span>
              </span>
              <Button
                variant="secondary"
                disabled={pending}
                iconEnd={<ArchiveIcon size={16} />}
                onClick={() => {
                  onArchive(candidate.id);
                }}
              >
                {t('searches.action.archive')}
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
